import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import MarkdownText from 'components/Widget/MarkdownText';
import { resolveDsPicksToDisplayNames } from 'designSystem/content/util';
import {
	ChatEntry,
	ChatEntryRestriction,
	ChatStatus,
	SystemChatEntry,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import { MenuItemAction } from 'menu/MenuItem';
import { useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem, selectActiveDsChat } from 'redux/db/reducers';
import {
	ChatMessageActionPart,
	ChatMessageDsUpdatePart,
	ChatMessageTextPart,
	designSystemEditingBlocked,
	isDsLegacyChat,
	splitMessageWithDsUpdates,
} from 'redux/db/util';
import { showContextMenu } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayPromptModal } from 'redux/mode/util';
import { displayDsReadOnlyModal } from 'redux/mode/util/modals';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { onFileDropped } from 'utils';
import DsChatInput from '../../ChatInput/DsChatInput';
import QueueIndicator from '../../QueueIndicator';
import useCopyMessage from '../../hooks/useCopyMessage';
import { useChatScroll } from '../../hooks/useChatScroll';
import * as Base from '../styles';
import FeedbackBar from '../FeedbackBar';
import UserMessage from '../UserMessage';
import StepHeading, { parseStepHeading } from '../StepHeading';
import DsActionCard from './DsActionCard';
import DsUpdateCard from './DsUpdateCard';

const DsConversation = () => {
	const containerRef = useRef<HTMLDivElement>(null);

	// spacer div we will use to force user's last input to scroll to the top of their screen when they submit a prompt
	const spacerRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();
	const theme = useTheme();
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const { onCopyMessage } = useCopyMessage(splitMessageWithDsUpdates);

	const { activeDsChat, designSystem, designSystemContent, isMobile, chatLoading } = useSelector(
		(r: RootReducer) => {
			const dsChat = selectActiveDsChat(r.db, r.mode.activeDsChatId);
			return {
				activeDsChat: dsChat,
				designSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
				designSystemContent: r.mode.designSystemContent,
				isMobile: selectIsMobile(r.mode),
				chatLoading: r.db.loading.activeDsChat !== 'loaded' && r.mode.activeDsChatId !== null,
			};
		},
		shallowEqual
	);

	// Filter to entries that actually render in the conversation. Invisible
	// entries (e.g. orchestrator-emitted markers with restriction:LLM) get
	// excluded so useChatScroll's spacer math isn't triggered by entries that
	// produce no DOM nodes. Without this filter, an invisible append causes the
	// spacer-eating effect to measure the previously-rendered system message
	// (the plan card) and shrink the spacer by its height, which clamps
	// scrollTop and visibly jumps the chat up.
	const visibleEntries = useMemo(
		() =>
			(activeDsChat?.entries ?? []).filter(
				(e) =>
					e.kind === 'user' || !e.restriction || e.restriction === ChatEntryRestriction.USER
			),
		[activeDsChat?.entries]
	);

	const lastChatMessage =
		visibleEntries.length > 0 ? visibleEntries[visibleEntries.length - 1].message : null;

	const isLegacy = useMemo(
		() => isDsLegacyChat(activeDsChat?.entries || []),
		[activeDsChat?.entries]
	);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	// Use shared scroll hook
	const { isScrolledToBottom, onJumpToBottom } = useChatScroll({
		containerRef,
		spacerRef,
		entries: visibleEntries,
		activeChatId: activeDsChat?.id || null,
		lastChatMessage,
		shouldSkipAutoScroll: () => {
			// Only skip if designSystem metadata is not loaded
			return !designSystem?.metadata;
		},
		isInitialized: !!designSystem?.metadata,
	});

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onContextMenuInMessage = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'ds',
		chatId: activeDsChat?.id || null,
	});

	const onFeedback = (entryIndex: number, feedback: 'helpful' | 'not_helpful') => {
		if (!activeDsChat) return;

		const blocked = designSystemEditingBlocked();
		if (blocked && blocked !== 'historical') {
			displayDsReadOnlyModal(blocked);
			return;
		}

		const entry = activeDsChat.entries[entryIndex] as SystemChatEntry;
		const newFeedback = entry.feedback === feedback ? null : feedback;

		forge.dsChatFeedback({ dsChatId: activeDsChat.id, entryIndex, feedback: newFeedback });

		if (newFeedback === 'not_helpful') {
			displayPromptModal({
				title: 'What went wrong?',
				description: 'Tell us what could be improved.',
				cancelLabel: 'Dismiss',
				submitLabel: 'Submit',
				isMultilineInput: true,
				onSubmit: (value?: string) => {
					if (value?.trim()) {
						forge.dsChatFeedback({
							dsChatId: activeDsChat.id,
							entryIndex,
							feedback: 'not_helpful',
							comment: value.trim(),
						});
					}
				},
			});
		}
	};

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);
		if (!activeDsChat) return;
		onFileDropped(fileCallbacks)(e);
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderEntries = (entries: ChatEntry[]) => {
		const isLastEntry = (index: number) => index === entries.length - 1;
		const isChatProcessing =
			activeDsChat?.metadata?.status === ChatStatus.PROCESSING ||
			activeDsChat?.metadata?.status === ChatStatus.STOPPABLE;

		return entries.map((entry, index) => {
			if (
				entry.kind === 'system' &&
				(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
			) {
				// Step headings announce the next plan step. They label the
				// response below, so no feedback bar, no copy, no bubble chrome.
				const stepHeading = parseStepHeading(entry.message);
				if (stepHeading) {
					return (
						<Base.SystemMessage key={index} isMobile={isMobile}>
							<StepHeading number={stepHeading.number} label={stepHeading.label} />
						</Base.SystemMessage>
					);
				}
				const parts = splitMessageWithDsUpdates(entry.message);
				// Skip entries with no visible content — e.g. a lone <plan-approved/>
				// marker gets stripped to empty and shouldn't render an empty bubble.
				if (parts.length === 0) return null;
				const systemEntry = entry as SystemChatEntry;
				return (
					<Base.SystemMessage
						key={index}
						className="system-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{parts.map((part, i) => {
							if (part.type === 'text') {
								const textPart = part as ChatMessageTextPart;
								return (
									<MarkdownText key={i} onContextMenu={onContextMenuInMessage}>
										{textPart.content}
									</MarkdownText>
								);
							} else if (part.type === 'action') {
								const actionPart = part as ChatMessageActionPart;
								return <DsActionCard key={i} action={actionPart.action} />;
							} else {
								const updatePart = part as ChatMessageDsUpdatePart;
								return (
									<DsUpdateCard
										key={i}
										section={updatePart.section}
										paths={updatePart.paths}
										isMobile={isMobile}
									/>
								);
							}
						})}
						<FeedbackBar
							feedback={systemEntry.feedback}
							isLastEntry={isLastEntry(index)}
							isGenerating={isLastEntry(index) && isChatProcessing}
							onCopy={() => onCopyMessage(index, entry.message)}
							onFeedback={(fb) => onFeedback(index, fb)}
						/>
					</Base.SystemMessage>
				);
			}
			if (entry.kind === 'user') {
				const userEntry = entry as UserChatEntry;
				const resolvedPicks = userEntry.selectedDsPicks?.length
					? resolveDsPicksToDisplayNames(userEntry.selectedDsPicks, designSystemContent ?? null)
					: [];
				return (
					<UserMessage
						key={index + 'user'}
						message={entry.message}
						isMobile={isMobile}
						attachments={userEntry.attachments}
						resolvedDsPicks={resolvedPicks.length > 0 ? resolvedPicks : undefined}
						onContextMenu={onContextMenuInMessage}
					/>
				);
			}
		});
	};

	const renderStatus = () => {
		if (!activeDsChat?.metadata?.statusMessage) return null;
		if (!activeDsChat?.metadata?.status) return null;

		const { status, statusMessage } = activeDsChat.metadata;

		if (status !== ChatStatus.PROCESSING && status !== ChatStatus.STOPPABLE) {
			return null;
		}

		return (
			<Base.StatusMessage isMobile={isMobile}>
				<Base.StatusText>{statusMessage}</Base.StatusText>
				<Loader size={16} />
			</Base.StatusMessage>
		);
	};

	if (!activeDsChat) {
		if (chatLoading) {
			return (
				<Base.Base>
					<Base.CenteredScrollingArea>
						<Loader size={24} />
					</Base.CenteredScrollingArea>
				</Base.Base>
			);
		}
		return null;
	}

	return (
		<>
			<Base.Base
				onDragOver={isLegacy ? undefined : onDragOver}
				onDragLeave={isLegacy ? undefined : onDragLeave}
				onDrop={isLegacy ? undefined : onDrop}
			>
				<Base.ScrollingArea ref={containerRef}>
					{renderEntries(activeDsChat.entries)}
					{!isLegacy && renderStatus()}
					<div ref={spacerRef} />
				</Base.ScrollingArea>
				{isDraggingFile && !isLegacy && (
					<Base.DragOverlay>
						<Base.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<Base.DragOverlayText isMobile={isMobile}>Drop files to attach</Base.DragOverlayText>
						</Base.DragOverlayContent>
					</Base.DragOverlay>
				)}
				{activeDsChat.entries.length > 0 && <Base.Spacer />}
				<Base.InputArea>
					{!isScrolledToBottom && (
						<Base.JumpButton onClick={onJumpToBottom} isMobile={isMobile}>
							<Base.JumpButtonIcon>
								<Icon
									type={IconType.LINE_ARROW_DOWN}
									width={isMobile ? 24 : 18}
									height={isMobile ? 24 : 18}
									color={theme.gray[700]}
								/>
							</Base.JumpButtonIcon>
						</Base.JumpButton>
					)}
					<QueueIndicator chatType="ds" chatId={activeDsChat.id} />
					<DsChatInput key={activeDsChat.id} dsChat={activeDsChat} isLegacy={isLegacy} />
				</Base.InputArea>
			</Base.Base>
		</>
	);
};

export default DsConversation;
